import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import React from "react";
import useResponsive from "../../../../hooks/useResponsive";
import { DateTimePicker } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";

interface ElectionDeadlineDialogProps {
  isElectionDeadlineDialogOpen: boolean;
  resetElectionDeadlineDialog: () => void;
  startDate: Dayjs | null;
  setElectionStart: (value: React.SetStateAction<dayjs.Dayjs | null>) => void;
  endDate: Dayjs | null;
  setElectionEnd: (value: React.SetStateAction<dayjs.Dayjs | null>) => void;
  handleElectionDeadlineSave: () => void;
}

/**
 * Dialog to set the election deadline for a trainee generation
 * @param isElectionDeadlineDialogOpen - A boolean indicating if the dialog is open
 * @param resetElectionDeadlineDialog - A function to reset the election deadline dialog, sets the start and end date to null
 * @param startDate - The start date of the election deadline
 * @param setElectionStart - A function to set the start date of the election deadline
 * @param endDate - The end date of the election deadline
 * @param setElectionEnd - A function to set the end date of the election deadline
 * @param handleElectionDeadlineSave - A function to handle the save action of the election deadline, usually calls the API to save the election deadline and closes the dialog
 * @returns A Dialog component to set the election deadline for a trainee generation
 */
const ElectionDeadlineDialog = ({
  isElectionDeadlineDialogOpen,
  resetElectionDeadlineDialog,
  startDate,
  setElectionStart,
  endDate,
  setElectionEnd,
  handleElectionDeadlineSave,
}: ElectionDeadlineDialogProps) => {
  const isMobile = useResponsive("down", "sm");

  const [startDateError, setStartDateError] = React.useState<boolean>(false);
  const [endDateError, setEndDateError] = React.useState<boolean>(false);

  const handleElectionStartChange = (date: Dayjs | null) => {
    if (endDate && date) {
      setEndDateError(false);
      setStartDateError(date.isAfter(endDate));
    }
    setElectionStart(date);
  };

  const handleElectionEndChange = (date: Dayjs | null) => {
    if (startDate && date) {
      setStartDateError(false);
      setEndDateError(date.isBefore(startDate));
    }
    setElectionEnd(date);
  };

  // Resets the election deadline dialog, sets the start and end date to null and resets the error states
  const handleClose = () => {
    setStartDateError(false);
    setEndDateError(false);
    resetElectionDeadlineDialog();
  };

  const saveElectionDeadline = () => {
    if (startDateError || endDateError || !startDate || !endDate) {
      return;
    }
    handleElectionDeadlineSave();
  };

  return (
    <Dialog open={isElectionDeadlineDialogOpen} onClose={handleClose}>
      <DialogTitle>Zeitraum ändern</DialogTitle>
      <DialogContent>
        <Stack spacing={1}>
          <Stack direction={isMobile ? "column" : "row"} spacing={2} alignItems={"center"}>
            <Typography flex={1} fontWeight={"bold"}>
              Startzeit:
            </Typography>
            <DateTimePicker
              sx={{ flex: 2, width: "100%" }}
              slotProps={{
                textField: {
                  variant: "outlined",
                  size: "small",
                  error: startDateError,
                  helperText: startDateError ? "Startzeit muss vor der Deadline liegen" : "",
                },
              }}
              value={startDate}
              onChange={handleElectionStartChange}
            />
          </Stack>
          <Stack direction={isMobile ? "column" : "row"} spacing={2} alignItems={"center"}>
            <Typography flex={1} fontWeight={"bold"}>
              Deadline:
            </Typography>
            <DateTimePicker
              sx={{ flex: 2, width: "100%" }}
              slotProps={{
                textField: {
                  variant: "outlined",
                  size: "small",
                  error: endDateError,
                  helperText: endDateError ? "Deadline muss nach der Startzeit liegen" : "",
                },
              }}
              value={endDate}
              onChange={handleElectionEndChange}
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Abbrechen</Button>
        <Button
          variant="contained"
          onClick={saveElectionDeadline}
          disabled={startDateError || endDateError || !startDate || !endDate}
        >
          Speichern
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ElectionDeadlineDialog;
